import { useState } from 'react';

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'software', label: 'Software' },
    { id: 'mobile', label: 'Mobile Apps' },
    { id: 'web', label: 'Web Apps' }
  ];

  const projects = [
    {
      title: 'FinTech Banking Platform',
      category: 'software',
      description: 'Enterprise-grade banking solution with real-time transactions and advanced security features',
      image: 'https://readdy.ai/api/search-image?query=Modern%20fintech%20banking%20application%20interface%20on%20laptop%20screen%20showing%20clean%20dashboard%20with%20financial%20charts%20and%20transaction%20data%2C%20professional%20banking%20software%20with%20teal%20and%20navy%20blue%20color%20scheme%2C%20minimalist%20UI%20design%20with%20simple%20white%20background%2C%20high-end%20enterprise%20application%20aesthetic&width=800&height=600&seq=portfolio-001&orientation=landscape',
      tags: ['React', 'Node.js', 'PostgreSQL'],
      stats: { users: '50K+', uptime: '99.9%' }
    },
    {
      title: 'Healthcare Management System',
      category: 'software',
      description: 'Comprehensive patient management and telemedicine platform for healthcare providers',
      image: 'https://readdy.ai/api/search-image?query=Clean%20healthcare%20management%20software%20interface%20displaying%20patient%20records%20and%20medical%20dashboard%20on%20desktop%20monitor%2C%20modern%20medical%20application%20with%20soft%20blue%20and%20white%20color%20scheme%2C%20professional%20healthcare%20technology%20with%20simple%20minimalist%20background%2C%20user-friendly%20medical%20software%20design&width=800&height=600&seq=portfolio-002&orientation=landscape',
      tags: ['Angular', 'Python', 'MongoDB'],
      stats: { providers: '200+', patients: '100K+' }
    },
    {
      title: 'E-Commerce Mobile App',
      category: 'mobile',
      description: 'Feature-rich shopping app with AI-powered recommendations and seamless checkout',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20e-commerce%20mobile%20app%20mockup%20on%20smartphone%20showing%20product%20gallery%20and%20shopping%20interface%2C%20modern%20retail%20application%20with%20vibrant%20product%20images%20on%20clean%20white%20background%2C%20professional%20mobile%20commerce%20design%20with%20teal%20accent%20colors%2C%20minimalist%20app%20interface%20aesthetic&width=800&height=600&seq=portfolio-003&orientation=landscape',
      tags: ['React Native', 'Firebase', 'Stripe'],
      stats: { downloads: '250K+', rating: '4.8★' }
    },
    {
      title: 'Fitness Tracking App',
      category: 'mobile',
      description: 'Personal fitness companion with workout plans, nutrition tracking, and progress analytics',
      image: 'https://readdy.ai/api/search-image?query=Modern%20fitness%20tracking%20mobile%20application%20on%20smartphone%20displaying%20workout%20statistics%20and%20health%20metrics%2C%20clean%20fitness%20app%20interface%20with%20energetic%20orange%20and%20teal%20colors%20on%20simple%20white%20background%2C%20professional%20health%20and%20wellness%20app%20design%20with%20minimalist%20aesthetic&width=800&height=600&seq=portfolio-004&orientation=landscape',
      tags: ['Flutter', 'AWS', 'HealthKit'],
      stats: { workouts: '1M+', users: '75K+' }
    },
    {
      title: 'Project Management Platform',
      category: 'web',
      description: 'Collaborative workspace for teams with task management, time tracking, and reporting',
      image: 'https://readdy.ai/api/search-image?query=Professional%20project%20management%20web%20application%20interface%20on%20laptop%20showing%20kanban%20board%20and%20task%20dashboard%2C%20modern%20productivity%20software%20with%20organized%20layout%20and%20teal%20accent%20colors%20on%20clean%20white%20background%2C%20enterprise%20collaboration%20tool%20with%20minimalist%20design%20aesthetic&width=800&height=600&seq=portfolio-005&orientation=landscape',
      tags: ['Vue.js', 'Express', 'Redis'],
      stats: { teams: '5K+', tasks: '500K+' }
    },
    {
      title: 'Real Estate Marketplace',
      category: 'web',
      description: 'Property listing platform with virtual tours, mortgage calculator, and agent matching',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20real%20estate%20marketplace%20website%20on%20desktop%20showing%20property%20listings%20and%20search%20interface%2C%20modern%20property%20platform%20with%20beautiful%20home%20images%20and%20clean%20layout%20on%20white%20background%2C%20professional%20real%20estate%20technology%20with%20teal%20and%20navy%20color%20scheme%2C%20minimalist%20web%20design&width=800&height=600&seq=portfolio-006&orientation=landscape',
      tags: ['Next.js', 'GraphQL', 'Mapbox'],
      stats: { listings: '50K+', agents: '2K+' }
    }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="portfolio" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-teal-600 font-medium mb-3 text-sm uppercase tracking-wider">Our Work</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Projects We're <span className="italic">Proud Of</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Explore our portfolio of successful digital solutions that have helped businesses transform and grow
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeFilter === filter.id
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100"
            >
              <div className="relative h-56 overflow-hidden bg-gray-100">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{project.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  {Object.entries(project.stats).map(([key, value], statIndex) => (
                    <div key={statIndex} className="text-center">
                      <div className="text-lg font-bold text-gray-900">{value}</div>
                      <div className="text-xs text-gray-500 capitalize">{key}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
